import { PerpsProvider } from '@kwenta/sdk/types'
import { type FC, useCallback, useMemo, useState } from 'react'

import SettingsIcon from 'assets/svg/app/settings.svg'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import { IS_ONE_CLICK_TRADING_ENABLED } from 'constants/defaults'
import {
	PROVIDER_CHAIN_NAMES,
	PROVIDER_MAINNET_CHAINS,
	SUPPORTED_CHAIN_IDS,
	SUPPORTED_CHAIN_IDS_MAINNET,
} from 'constants/network'
import Connector from 'containers/Connector'
import useClickOutside from 'hooks/useClickOutside'
import useNetworkSwitcher from 'hooks/useNetworkSwitcher'
import WatchOrConnectWallet, {
	WatcherModeIcon,
} from 'sections/futures/WatcherMode/WatchOrConnectWallet'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectSnxV2Account } from 'state/futures/isolatedMargin/selectors'
import { selectShowTestnets } from 'state/futures/selectors'
import { selectSnxV3Account } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled, selectIsWatcherMode } from 'state/wallet/selectors'
import media from 'styles/media'

import BalanceActions from './BalanceActions'
import ConnectionDot from './ConnectionDot'
import NetworksSwitcher from './NetworksSwitcher'
import OneClickTradingActions from './OneClickTradingActions'
import WalletActions from './WalletActions'

import dynamic from 'next/dynamic'
import { setOpenModal } from 'state/app/reducer'
const NotifiBellIcon = dynamic(() => import('notifi/BellIcon'), { ssr: false })
const NotifiContextWrapper = dynamic(() => import('notifi/NotifiContextWrapper'), { ssr: false })

const WalletButtons: FC = () => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()
	const { network, isWalletConnected, login } = Connector.useContainer()
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const smartMarginAccount = useAppSelector(selectSnxV2Account)
	const crossMarginAccount = useAppSelector(selectSnxV3Account)

	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const showTestnets = useAppSelector(selectShowTestnets)
	const perpsProvider = useAppSelector(selectPerpsProvider)

	const hasAccount = useMemo(() => {
		let hasAccount = false
		switch (perpsProvider) {
			case PerpsProvider.SNX_V2_OP:
				hasAccount = !!smartMarginAccount
				break
			case PerpsProvider.SNX_V3_BASE:
				hasAccount = !!crossMarginAccount
				break
			default:
				hasAccount = false
				break
		}
		return hasAccount
	}, [perpsProvider, smartMarginAccount, crossMarginAccount])

	const [openConnectionDropdown, setOpenConnectionDropdown] = useState(false)
	const { ref } = useClickOutside(() => setOpenConnectionDropdown(false))

	const switchButtonLabel = t('homepage.l2.cta-buttons.switch-l2', {
		chain: PROVIDER_CHAIN_NAMES[perpsProvider],
	})

	const { switchToL2 } = useNetworkSwitcher(PROVIDER_MAINNET_CHAINS[perpsProvider])

	const supportedNetwork = useMemo(
		() =>
			showTestnets
				? SUPPORTED_CHAIN_IDS.includes(network?.id as number)
				: SUPPORTED_CHAIN_IDS_MAINNET.includes(network?.id as number),
		[network?.id, showTestnets]
	)

	const handleOpenConnectModal = useCallback(() => {
		login?.()
	}, [login])

	const WalletIsNotConnected = useCallback(
		() => (
			<>
				<Button
					size="small"
					noOutline
					onClick={handleOpenConnectModal}
					data-testid="connect-wallet"
					mono
				>
					<ConnectionDot />
					{t('common.wallet.connect')}
				</Button>
				<Button size="small" noOutline onClick={() => setOpenConnectionDropdown((x) => !x)} mono>
					<WatcherModeIcon />
					{t('common.wallet.watch')}
				</Button>
			</>
		),
		[handleOpenConnectModal, t]
	)

	const WalletIsConnectedButNotSupported = useCallback(
		() => (
			<>
				<Button size="small" data-testid="unsupported-network" mono>
					<ConnectionDot />
					{t('common.wallet.unsupported-network')}
				</Button>
				<Button size="small" mono onClick={switchToL2}>
					{switchButtonLabel}
				</Button>
			</>
		),
		[t, switchButtonLabel, switchToL2]
	)

	const WalletIsConnectedAndSupported = useCallback(
		() => (
			<>
				<BalanceActions />
				{!isWatcherMode && (
					<>
						{!abstractionWalletEnabled && <NetworksSwitcher />}
						{!!hasAccount && IS_ONE_CLICK_TRADING_ENABLED && !abstractionWalletEnabled && (
							<OneClickTradingActions />
						)}
					</>
				)}
				<WalletActions />
			</>
		),
		[isWatcherMode, hasAccount, abstractionWalletEnabled]
	)

	return (
		<Container ref={ref}>
			{isWalletConnected ? (
				supportedNetwork ? (
					<WalletIsConnectedAndSupported />
				) : (
					<WalletIsConnectedButNotSupported />
				)
			) : (
				<WalletIsNotConnected />
			)}
			<MenuButton onClick={() => dispatch(setOpenModal('preferences'))} noOutline>
				<SettingsIcon width={17} height={18} />
			</MenuButton>
			{openConnectionDropdown && (
				<WatchOrConnectWalletContainer>
					<WatchOrConnectWallet onDismiss={() => setOpenConnectionDropdown(false)} />
				</WatchOrConnectWalletContainer>
			)}
			<NotifiContextWrapper>
				<NotifiBellIcon />
			</NotifiContextWrapper>
		</Container>
	)
}

const Container = styled.div`
	display: grid;
	grid-gap: 15px;
	grid-auto-flow: column;
`

const WatchOrConnectWalletContainer = styled(BaseCard)`
	z-index: 100;
	position: fixed;
	top: 15px;
	right: 70px;
	display: flex;
	flex-direction: column;
	width: 300px;
	row-gap: 20px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`
const MenuButton = styled(Button)`
	display: grid;
	place-items: center;
	height: 41px;
	width: 41px;
	padding: 0px;

	svg {
		path {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.inactive};
		}
	}
`

export default WalletButtons
